
/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */

import React, { useCallback, useEffect, useState, useMemo, useReducer, RefObject, CSSProperties, MouseEvent } from 'react';
import { styled } from '@mui/material/styles';

import Popper, { PopperProps } from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FlipToFront from '@mui/icons-material/FlipToFront';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCut from '@mui/icons-material/ContentCut';
import DashboardCustomize from '@mui/icons-material/DashboardCustomize';

import InfoIcon from '@/components/icons/Info';
import HomeIcon from './icons/Home.js';
import OrbitalIcon from './icons/Orbital.js';
import FPVIcon from './icons/FPV.js';
import GearIcon from '@/components/icons/Gear';
import ScissorsIcon from './icons/Scissors.js';
import HighlightIcon from './icons/Highlight.js';
import SelectByTypeIcon from './icons/SelectByType.js';
import HideIcon from './icons/Hide.js';
import ShowIcon from './icons/Show.js';

import { ProjectData } from '@/state/types/project-data';

import {
  Action,
  IsolationMode,
  NavMode,
  ElementDetails,
  ProgressInfo,
  ViewerSettings,
  ViewerControllerInterface,
  Viewer as RawViewer,
  LogLevel,
  // AddGraphicsParams
} from '@bimdata/viewer-react';

import { item as getElement } from '@/loaders/element/index';
// import { CustomPalette } from '@/theme';
import { RootState, useAppSelector } from '@/state/store';
import ViewerFloatingWindows from './tools/ViewerFloatingWindows';
import { createSelector } from '@reduxjs/toolkit';
import { ClippingPlane, Vector3d, ViewParams } from '@bimdata/definitions/types';
import Toolbox from './tools/Toolbox.js';
import ToolbarButton from './tools/ToolbarButton';
import ClippingManager from './tools/ClippingManager';
import t from '@/utils/translate';
import api from '@/api/api';

const ToolbarSeparator = styled('hr')({
  height: '44px',
  margin: 0,
  borderStyle: 'solid',
  borderColor: '#091E421A',
  borderWidth: '0 0 0 1px',
});

const ToolbarButtonGroup = styled('div')({
  display: 'flex',
  position: 'relative',
  
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    boxShadow: '0px 0px 0px 1px #091e421a inset',
    borderRadius: '8px',
    pointerEvents: 'none',
  },

  '&>button': {
    borderRadius: 0,
    '&:first-of-type': {
      borderTopLeftRadius: '8px',
      borderBottomLeftRadius: '8px',
    },
    '&:last-of-type': {
      borderTopRightRadius: '8px',
      borderBottomRightRadius: '8px',
    },

    '&:not(:disabled,.disabled):active, &.pressed': {
      boxShadow: '1px 1px 3px 0px #091E4240 inset'
    },
  }
});

type ClippingState = {
  planes: ClippingPlane[],
  selected: string | null,
  flipped: string[],
  editor: boolean,
  visible: boolean,
};
type ClippingStateActions = {
  type: 'updateOrInsertPlane',
  payload: ClippingPlane,
} | {
  type: 'setEditorState',
  payload: boolean,
} | {
  type: 'setClippingVisible',
  payload: boolean,
} | {
  type: 'setSelectedPlane',
  payload: string | null
} | {
  type: 'removePlanes',
  payload: string[]
} | {
  type: 'togglePlaneFlipping',
  payload: string,
};

function clippingStateReducer(state: ClippingState, { type, payload }: ClippingStateActions) {
  if (type === 'updateOrInsertPlane') {
    const i = state.planes.findIndex((v) => v.id === payload.id);
    let planes = state.planes;
    if (i < 0) {
      planes = [
        ...state.planes,
        payload,
      ];
    } else {
      planes = state.planes.slice();
      planes.splice(i, 1, payload);
    }

    return {
      ...state,
      planes,
    };
  }

  if (type === 'setSelectedPlane' && state.selected !== payload) {
    return {
      ...state,
      selected: payload
    };
  }

  if (type === 'setEditorState' && state.editor !== payload) {
    return {
      ...state,
      editor: payload
    };
  }

  if (type === 'setClippingVisible' && state.visible !== payload) {
    return {
      ...state,
      visible: payload
    };
  }

  if (type === 'removePlanes' && state.planes.some((cp) => payload.includes(cp.id))) {
    const planes = state.planes.filter((cp) => !payload.includes(cp.id));
    return {
      ...state,
      planes,
    };
  }

  if (type === 'togglePlaneFlipping' && state.planes.some((cp) => cp.id === payload)) {
    const i = state.flipped.findIndex((f) => f === payload);
    let flipped = state.flipped;
    if (i < 0) {
      flipped = [
        ...state.flipped,
        payload
      ];
    } else {
      flipped = [ ...state.flipped ];
      flipped.splice(i, 1);
    }
    return {
      ...state,
      flipped,
    };
  }

  return state;
}

export type ViewerProps = {
  project: ProjectData;
  filter: number[] | null;
  onLoadStarted?: (controller: ViewerControllerInterface) => void;
  onLoadProgress?: (info: ProgressInfo, controller: ViewerControllerInterface) => void;
  onLoadFinished?: (models: number[] | undefined, controller: ViewerControllerInterface) => void;
  onHome?: (position: Vector3d, direction: Vector3d, controller: ViewerControllerInterface) => void;
  viewParams?: ViewParams;
  accessToken?: string;
  setSetting: (name: string, value: unknown) => void;
  controllerRef?: RefObject<ViewerControllerInterface | null>;
};

type EventHandlersProvider = {
  onLoadStarted?: (controller: ViewerControllerInterface) => void;
  onLoadProgress?: (info: ProgressInfo, controller: ViewerControllerInterface) => void;
  onLoadFinished?: (models: number[] | undefined, controller: ViewerControllerInterface) => void;
  onHome?: (position: Vector3d, direction: Vector3d, controller: ViewerControllerInterface) => void;
};

const LOG_LEVEL = import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.NONE;

const VIEWER_STYLES: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
};

const WINDOWS_STYLES: CSSProperties = {
  position: 'absolute',
  top: 10,
  left: 10,
  bottom: 80,
  right: 10,
};

const CONTEXT_MENU_STYLES: CSSProperties = { zIndex: 10 };

const GROW_STYLES: CSSProperties = { transformOrigin: 'left top' };

export const Viewer = ({
  project, filter, onLoadStarted, onLoadProgress, onLoadFinished,
  onHome, viewParams, accessToken, setSetting, controllerRef,
} : ViewerProps) => {

  const { settings, devMode } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.clientState.settings as ViewerSettings,
        (state: RootState) => state.authentication.clientState.developmentSettingsVisibility,
      ],
      (settings, devMode) => ({ settings, devMode })
    )
  );

  // const dummyBlock = React.createRef<HTMLCanvasElement>();

  const [controller, setController] = useState<ViewerControllerInterface | null>(null);

  const [loadedModels, setLoadedModels] = useState<number[]>([]);

  const [loadingState] = useState<{
    progress: number;
    loading: boolean;
    processing: boolean;
    loaded: boolean;
  }>({
    progress: 0,
    loaded: true,
    loading: false,
    processing: false,
  });

  const [mode, setMode] = useState<NavMode>(NavMode.ORBITAL);

  const [isolationMode, setIsolationMode] = useState<IsolationMode>(IsolationMode.NONE);

  const [selected, setSelected] = useState<number[]>([]);

  const [hidden, setHidden] = useState<boolean>(false);

  const [events] = useState<EventHandlersProvider>({});
  events.onHome = onHome;
  events.onLoadFinished = onLoadFinished;
  events.onLoadProgress = onLoadProgress;
  events.onLoadStarted = onLoadStarted;

  // const [color, setColor] = React.useState<string>('#ffffff');

  const [ clippingState, clippingStateDispatch ] = useReducer(clippingStateReducer, {
    planes: [],
    flipped: [],
    selected: null,
    editor: false,
    visible: !!viewParams?.clipping
  });

  const [elementDetails, setElementDetails] = useState<ElementDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

  const [contextMenuAnchor, setContextMenuAnchor] = React.useState<PopperProps['anchorEl']>(null);
  const [contextMenuTarget, setContextMenuTarget] = React.useState<number | null>(null);
  const [contextMenuTargetType, setContextMenuTargetType] = React.useState<'element' | null>(null);

  const loadElementDetails = useCallback(async (projectId: string, elementId: number) => {
    const element = await getElement(projectId, elementId, accessToken);
    setElementDetails(element);
  }, [accessToken]);

  const resetContextMenu = useCallback(() => {
    setContextMenuAnchor(null);
    setContextMenuTargetType(null);
    setContextMenuTarget(null);
  }, []);

  const onViewerReady = useCallback((vc: ViewerControllerInterface) => {
    vc.addEventListener('load', ({ controller }) => {
      events.onLoadStarted && events.onLoadStarted(controller);
    });

    vc.addEventListener('load-progress', ({ info, controller }) => {
      events.onLoadProgress && events.onLoadProgress(info, controller);
    });

    vc.addEventListener('ready', ({ models, controller }) => {
      if (models) {
        setLoadedModels(models);
      }
      events.onLoadFinished && events.onLoadFinished(models, controller);
    });

    vc.addEventListener('selected', ({ selection }) => {
      setSelected(selection);
    });

    vc.addEventListener('isolation', ({ mode }) => {
      setIsolationMode(mode);
    });

    vc.addEventListener('clipping-plane-updated', ({ plane }) => {
      clippingStateDispatch({ type: 'updateOrInsertPlane', payload: plane });
    });

    vc.addEventListener('clipping-plane-selected', ({ selection }) => {
      clippingStateDispatch({ type: 'setSelectedPlane', payload: selection });
    });

    vc.addEventListener('clipping-planes-delete', ({ plane }) => {
      clippingStateDispatch({ type: 'removePlanes', payload: !Array.isArray(plane) ? [plane] : plane });
    });

    vc.addEventListener('clip-intersection-change', ({ state }) => {
      setSetting('clipIntersection', state);
    });

    vc.addEventListener('context-menu', ({ params }) => {
      setContextMenuAnchor({
        getBoundingClientRect: () => ({
          x: params.clientX,
          y: params.clientY,
          toJSON: () => '',
          width: 0,
          height: 0,
          left: params.clientX,
          right: params.clientX,
          top: params.clientY,
          bottom: params.clientY,
        })
      });
      setContextMenuTargetType(params.targetType);
      if (params.targetType === 'element') {
        setContextMenuTarget(params.elementId!);
      }
    });

    vc.addEventListener('home', ({ position, direction, controller }) => {
      events.onHome && events.onHome(position, direction, controller);
    });

    // @ts-ignore
    window.viewer = vc;
    /*
    // @ts-ignore
    window.addGraphics = function () {
      const box = new BoxGeometry(200, 200, 200);
      vc.action(Action.ADD_ELEMENTS, {
        geometry: {
          id: 200,
          data: {
            primitives: [
              {
                type: PrimitiveType.FACE,
                material: 0,
                position: new Float32Array(box.attributes.position.array),
                indices: new Uint32Array(box.index!.array),
                uv: new Float32Array(box.attributes.uv.array),
              }
            ]
          }
        },
        materials: [
          {
            color: [255, 0, 0],
            type: MaterialType.FACE,
          },
        ],
        elements: [
          {
            id: 444,
            matrix: (new Matrix4()).elements,
          }
        ]
      } as AddGraphicsParams);
    };
    // @ts-ignore
    window.removeGraphics = function () {
      vc.action(Action.REMOVE_ELEMENTS, [444]);
    };
    */
    setController(vc);
    if (controllerRef) {
      controllerRef.current = vc;
    }
  }, []);

  useEffect(() => {
    if (detailsOpen) {
      if (!selected.length) {
        setElementDetails(null);
      } else if (!elementDetails || elementDetails.id !== selected[0]) {
        loadElementDetails(project.code, selected[0]);
      }
    }
  }, [ project.code, selected, detailsOpen, elementDetails ]);

  useEffect(() => {
    if (
      contextMenuAnchor
      && (
        contextMenuTargetType !== 'element'
        || !contextMenuTarget
        || !selected.includes(contextMenuTarget)
      )
    ) {
      resetContextMenu();
    }
  }, [ contextMenuAnchor, contextMenuTargetType, contextMenuTarget, selected ]);

  useEffect(() => {
    if (detailsOpen && (loadingState.loading || loadingState.processing || !loadedModels.length)) {
      setDetailsOpen(false);
    }
    if (!loadedModels.length) {
      setElementDetails(null);
      setSelected([]);
    }
  }, [ loadingState.loading, loadingState.processing, loadedModels ]);

  const perform = useCallback((type: Action, data?: any) => {
    if (controller) {
      controller.action(type as any, data);
    }
  }, [controller]);

  const toggleDetails = useCallback(() => {
    setDetailsOpen(!detailsOpen);
    if (!detailsOpen && selected.length === 1) {
      loadElementDetails(project.code, selected[0]);
    }
  }, [detailsOpen, selected, project.code])

  const setClippingVisible = useCallback((state: boolean) => {
    perform(Action.CLIPPING_VISIBLE, state);
    clippingStateDispatch({ type: 'setClippingVisible', payload: state });
  }, [ perform, clippingStateDispatch ]);

  const setClippingEditor = useCallback((state: boolean) => {
    perform(Action.CLIPPING_EDITOR, state);
    clippingStateDispatch({ type: 'setEditorState', payload: state });
  }, [ perform, clippingStateDispatch ]);

  const onFlipPlane = useCallback((id: string) => {
    clippingStateDispatch({ type: 'togglePlaneFlipping', payload: id });
  }, [ clippingStateDispatch ]);

  const setNavMode = useCallback((m: NavMode) => {
    if (mode !== m) {
      perform(Action.MODE, m);
      setMode(m);
    }
  }, [ mode, perform, setMode ]);
  
  const performIsolationMode = useCallback((m: IsolationMode) => {
    if (isolationMode === m) {
      m = IsolationMode.NONE;
    }
    setIsolationMode(m);
    perform(Action.ISOLATE, m);
  }, [ isolationMode, perform ]);

  const toolbarDisabled = useMemo(
    () => (loadingState.loading || loadingState.processing || !loadedModels.length),
    [ loadedModels.length, loadingState.loading, loadingState.processing ]
  );

  const onCloseDetails = useCallback(() => setDetailsOpen(false), []);
  const onCloseSettings = useCallback(() => setSettingsOpen(!settingsOpen), [settingsOpen]);

  const tbHome = useCallback(() => perform(Action.FIT), [perform]);
  const tbOrbital = useCallback(() => setNavMode(NavMode.ORBITAL), [setNavMode]);
  const tbFPV = useCallback(() => setNavMode(NavMode.FPV), [setNavMode]);
  const tbCut = useCallback(() => performIsolationMode(IsolationMode.CUT), [performIsolationMode]);
  const tbHighlight = useCallback(() => performIsolationMode(IsolationMode.HIGHLIGHT), [performIsolationMode]);
  const tbExpandSelection = useCallback(() => perform(Action.SELECT_BY_TYPE), [perform]);
  const tbHide = useCallback(() => { perform(Action.HIDE); setHidden(true); }, [perform]);
  const tbShow = useCallback(() => { perform(Action.SHOW_ALL); setHidden(false); }, [perform]);
  const tbToogleSettings = useCallback(() => setSettingsOpen(!settingsOpen), [settingsOpen]);

  const onContextMenu = useCallback((event: MouseEvent) => event.preventDefault(), []);

  const ctxMenuDetailsWindow = useCallback(() => {
    resetContextMenu();
    if (!detailsOpen) toggleDetails();
  }, [resetContextMenu, detailsOpen, toggleDetails]);

  const ctxMenuHideElements = useCallback(() => {
    perform(Action.HIDE);
    setHidden(true);
    resetContextMenu();
  }, [perform, resetContextMenu]);

  const ctxMenuCutElement = useCallback(() => {
    if (isolationMode == IsolationMode.CUT) {
      perform(Action.ISOLATE, IsolationMode.NONE);
    } else {
      perform(Action.ISOLATE, IsolationMode.CUT);
    }
    resetContextMenu();
  }, [isolationMode, perform, resetContextMenu]);
  
  const ctxMenuHighlightElement = useCallback(() => {
    if (isolationMode == IsolationMode.HIGHLIGHT) {
      perform(Action.ISOLATE, IsolationMode.NONE);
    } else {
      perform(Action.ISOLATE, IsolationMode.HIGHLIGHT);
    }
    resetContextMenu();
  }, [isolationMode, perform, resetContextMenu]);
  
  const ctxMenuExpandSelection = useCallback(() => {
    perform(Action.SELECT_BY_TYPE);
    resetContextMenu();
  }, [perform, resetContextMenu]);


  return (
    <RawViewer
      style={VIEWER_STYLES}
      api={api}
      project={project.code}
      filter={filter}
      navMode={mode}
      onReady={onViewerReady}
      viewParams={viewParams}
      accessToken={accessToken}
      logLevel={devMode ? LogLevel.DEBUG : LOG_LEVEL}
      {...settings}
    >
      <Toolbox>
        <ToolbarButton
          hint={t('viewer.action.home')}
          onClick={tbHome}
          disabled={toolbarDisabled}
        >
          <HomeIcon />
        </ToolbarButton>
        <ToolbarButtonGroup>
          <ToolbarButton
            hint={t('viewer.action.orbital')}
            disabled={toolbarDisabled || clippingState.editor}
            active={mode === NavMode.ORBITAL}
            onClick={tbOrbital}
          >
            <OrbitalIcon/>
          </ToolbarButton>
          <ToolbarButton
            hint={t('viewer.action.fpv')}
            disabled={toolbarDisabled || clippingState.editor}
            active={mode === NavMode.FPV}
            onClick={tbFPV}
          >
            <FPVIcon/>
          </ToolbarButton>
        </ToolbarButtonGroup>
        <ToolbarButtonGroup>
          <ToolbarButton
            hint={t('viewer.action.hideRest')}
            disabled={
              (isolationMode === IsolationMode.NONE)
              && (toolbarDisabled || clippingState.editor || !selected.length)
            }
            active={isolationMode === IsolationMode.CUT}
            onClick={tbCut}
          >
            <ScissorsIcon/>
          </ToolbarButton>
          <ToolbarButton
            value={IsolationMode.HIGHLIGHT}
            hint={t('viewer.action.suppressRest')}
            disabled={
              (isolationMode === IsolationMode.NONE)
              && (toolbarDisabled || clippingState.editor || !selected.length)
            }
            active={isolationMode === IsolationMode.HIGHLIGHT}
            onClick={tbHighlight}
          >
            <HighlightIcon />
          </ToolbarButton>
        </ToolbarButtonGroup>
        <ToolbarButton
          hint={t('viewer.action.selectSameType')}
          disabled={toolbarDisabled || clippingState.editor || !selected.length || isolationMode != IsolationMode.NONE}
          onClick={tbExpandSelection}
        >
          <SelectByTypeIcon/>
        </ToolbarButton>
        {/*
        devSettingsVisibility && (
          <ToolbarButton
            hint=t('viewer.action.selectSameView')
            disabled={toolbarDisabled || clippingEditor || !selected.length || isolationMode != IsolationMode.NONE}
            onClick={() => perform(Action.SELECT_BY_GEOMETRY)}
          >
            <SelectAllIcon />
          </ToolbarButton>
        )
        */}
        <ToolbarButtonGroup>
          <ToolbarButton
            hint={t('viewer.action.hideSelected')}
            disabled={toolbarDisabled || clippingState.editor || isolationMode != IsolationMode.NONE || !selected.length}
            onClick={tbHide}
          >
            <HideIcon/>
          </ToolbarButton>
          <ToolbarButton
            hint={t('viewer.action.showHidden')}
            disabled={toolbarDisabled || clippingState.editor || isolationMode != IsolationMode.NONE || !hidden}
            indicator={hidden || null}
            onClick={tbShow}
          >
            <ShowIcon/>
          </ToolbarButton>
        </ToolbarButtonGroup>
        <ClippingManager
          disabled={toolbarDisabled}
          active={clippingState.editor}
          indicator={clippingState.visible}
          planes={clippingState.planes}
          flipped={clippingState.flipped}
          selected={clippingState.selected}
          clipIntersection={!!settings.clipIntersection}
          perform={perform}
          setEditorVisibility={setClippingEditor}
          setClippingVisibility={setClippingVisible}
          onFlip={onFlipPlane}
        />
        <ToolbarSeparator/>
        <ToolbarButton
          hint={t('viewer.action.elementProps')}
          disabled={toolbarDisabled}
          active={detailsOpen}
          onClick={toggleDetails}
        >
          <InfoIcon />
        </ToolbarButton>
        <ToolbarSeparator/>
        <ToolbarButton
          hint={t('viewer.action.settings')}
          disabled={loadingState.loading || loadingState.processing}
          active={settingsOpen}
          onClick={tbToogleSettings}
        >
          <GearIcon />
        </ToolbarButton>
        {/*
        devSettingsVisibility && (
          <ToolbarButton
            hint={!!timeline ? t('viewer.action.closeTimeline') : t('viewer.action.openTimeline')}
            disabled={loadingState.loading || loadingState.processing || !loadedModels.length}
            active={!!timeline}
            onClick={toggleTimeline}
          >
            {!!timeline ? <CloseIcon /> : <ViewTimelineOutlinedIcon />}
          </ToolbarButton>
        )
        */}
      </Toolbox>
      {controller && (
        <ViewerFloatingWindows
          detailsOpen={detailsOpen}
          elementDetails={elementDetails}
          selectionLength={selected.length}
          onCloseDetails={onCloseDetails}
          settingsOpen={settingsOpen}
          viewer={controller}
          onCloseSettings={onCloseSettings}
          minLayer={3}
          style={WINDOWS_STYLES}
        />
      )}
      <Popper
        open={!!contextMenuAnchor}
        anchorEl={contextMenuAnchor}
        placement="bottom-start"
        disablePortal
        transition
        style={CONTEXT_MENU_STYLES}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={GROW_STYLES}>
            <Paper onContextMenu={onContextMenu}>
              <ClickAwayListener onClickAway={resetContextMenu} mouseEvent="onMouseDown">
                <MenuList >
                  <MenuItem
                    disabled={selected.length > 1}
                    onClick={ctxMenuDetailsWindow}
                    divider
                  >
                    <ListItemIcon>
                      <InfoIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText>{t('viewer.action.elementProps')}</ListItemText>
                  </MenuItem>

                  <MenuItem
                    disabled={clippingState.editor || isolationMode != IsolationMode.NONE}
                    onClick={ctxMenuHideElements}
                  >
                    <ListItemIcon>
                      <VisibilityOff
                        color={
                          !clippingState.editor && isolationMode === IsolationMode.NONE
                            ? 'primary'
                            : 'secondary'
                        }
                      />
                    </ListItemIcon>
                    <ListItemText>
                      {t('viewer.action.hideElement', { count: selected.length })}
                    </ListItemText>
                  </MenuItem>

                  <MenuItem
                    disabled={clippingState.editor}
                    selected={isolationMode == IsolationMode.CUT}
                    onClick={ctxMenuCutElement}
                  >
                    <ListItemIcon>
                      <ContentCut color={!clippingState.editor ? 'primary' : 'secondary'} />
                    </ListItemIcon>
                    <ListItemText>
                      {isolationMode == IsolationMode.CUT ? t('viewer.action.show') : t('viewer.action.hide')}&nbsp;{t('viewer.term.rest')}
                    </ListItemText>
                  </MenuItem>

                  <MenuItem
                    disabled={clippingState.editor}
                    selected={isolationMode == IsolationMode.HIGHLIGHT}
                    onClick={ctxMenuHighlightElement}
                    divider
                  >
                    <ListItemIcon>
                      <FlipToFront color={!clippingState.editor ? 'primary' : 'secondary'} />
                    </ListItemIcon>
                    <ListItemText>
                      {isolationMode == IsolationMode.HIGHLIGHT ? t('viewer.action.reset') : t('viewer.action.shadow')}&nbsp;{t('viewer.term.rest')}
                    </ListItemText>
                  </MenuItem>

                  <MenuItem
                    disabled={clippingState.editor || isolationMode != IsolationMode.NONE}
                    onClick={ctxMenuExpandSelection}
                  >
                    <ListItemIcon>
                      <DashboardCustomize
                        color={
                          !clippingState.editor && isolationMode === IsolationMode.NONE
                            ? 'primary'
                            : 'secondary'
                        }
                      />
                    </ListItemIcon>
                    <ListItemText>{t('viewer.action.selectSameTypeShort')}</ListItemText>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </RawViewer>
  );
};
